import { onMounted, onUnmounted, readonly, ref } from "vue";

export type Appearance = "light" | "dark" | "system";

const mediaQuery = () => {
  if (typeof window === "undefined") return null;
  return window.matchMedia("(prefers-color-scheme: dark)");
};

const applyTheme = () => {
  const savedAppearance = localStorage.getItem("appearance") as Appearance | null;
  const preference = savedAppearance ?? "system";

  let isDark = false;
  if (preference === "dark") {
    isDark = true;
  } else if (preference === "system") {
    isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  document.documentElement.classList.toggle("dark", isDark);
};

const handleSystemThemeChange = () => {
  applyTheme();
};

let initialized = false;

export function initializeTheme() {
  applyTheme();

  if (initialized) return;
  mediaQuery()?.addEventListener("change", handleSystemThemeChange);
  initialized = true;
}

export function useAppearance() {
  const appearance = ref<Appearance>("system");

  const updateAppearance = (mode: Appearance) => {
    appearance.value = mode;

    if (mode === "system") {
      localStorage.removeItem("appearance");
    } else {
      localStorage.setItem("appearance", mode);
    }

    applyTheme();
  };

  onMounted(() => {
    const savedAppearance = (localStorage.getItem("appearance") as Appearance | null) ?? "system";
    updateAppearance(savedAppearance);
    mediaQuery()?.addEventListener("change", handleSystemThemeChange);
  });

  onUnmounted(() => {
    mediaQuery()?.removeEventListener("change", handleSystemThemeChange);
  });

  return {
    appearance: readonly(appearance),
    updateAppearance,
  } as const;
}
